import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {subscribeNewsletter, type Category, type I18nKey} from "@corporatesclub/shared";
import {paths} from "@/lib/paths";
import {getCategoriesCached} from "@/lib/shopCache";
import {useI18n} from "./I18nProvider";

const VALUE_ITEMS: {icon: string; key: I18nKey}[] = [
  {icon: "local_shipping", key: "Footer value free shipping"},
  {icon: "inventory_2", key: "Footer value products"},
  {icon: "eco", key: "Footer value trusted"},
  {icon: "verified_user", key: "Footer value secure payment"}
];

const ABOUT_LINKS: {key: I18nKey; to: string}[] = [
  {key: "FAQ", to: paths.faq},
  {key: "Contact us", to: paths.contact},
  {key: "Discounts", to: paths.promotions},
  {key: "Best Sellers", to: paths.bestSellers},
  {key: "New", to: paths.new}
];

const INFO_LINKS: {key: I18nKey; to: string}[] = [
  {key: "Payment & Delivery", to: paths.paymentDelivery},
  {key: "Terms & Conditions", to: paths.terms},
  {key: "Privacy Policy", to: paths.privacy},
  {key: "Return Policy", to: paths.returnsPolicy},
  {key: "Product return form", to: paths.returnForm},
  {key: "Contact us", to: paths.contact}
];

const ANPC_BADGES = [
  {href: "https://ec.europa.eu/consumers/odr/main/index.cfm?event=main.home2.show&lng=RO", src: "/images/anpc-sol.png", alt: "SOL"},
  {href: "https://anpc.ro/ce-este-sal/", src: "/images/anpc-sal.png", alt: "ANPC SAL"}
] as const;

function FooterLink({to, children, className = "cc-footer__link anim"}: {to: string; children: React.ReactNode; className?: string}) {
  if(/^https?:\/\//.test(to)) {
    return <a href={to} className={className} target="_blank" rel="noreferrer">{children}</a>;
  }
  return <Link to={to} className={className}>{children}</Link>;
}

function FooterNewsletter() {
  const {t} = useI18n();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "exists" | "error">("idle");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if(!email.trim() || status === "loading") return;
    setStatus("loading");
    try {
      await subscribeNewsletter({email: email.trim(), nume: email.trim().split("@")[0] || "Subscriber"});
      setStatus("ok");
      setEmail("");
    } catch(err) {
      const msg = err instanceof Error ? err.message : "";
      setStatus(msg.includes("subscribed") || msg.includes("EXISTS") ? "exists" : "error");
    }
  }

  return (
    <section className="cc-footerNewsletter" aria-label="Newsletter">
      <div className="inside cc-footerNewsletter__inner">
        <div className="cc-footerNewsletter__formWrap">
          <h2 className="cc-footerNewsletter__title">{t("Footer newsletter title")}</h2>
          <p className="cc-footerNewsletter__subtitle">{t("Footer newsletter subtitle")}</p>
          <form className="cc-footerNewsletter__form" onSubmit={onSubmit}>
            <label className="cc-footerNewsletter__field">
              <span className="visually-hidden">{t("Your e-mail address")}</span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); if(status !== "idle" && status !== "loading") setStatus("idle"); }}
                placeholder={t("Footer newsletter placeholder")}
                className="cc-footerNewsletter__input"
              />
            </label>
            <button type="submit" className="cc-footerNewsletter__submit" disabled={status === "loading"}>
              <span className="material-symbols-rounded" aria-hidden="true">mail</span>
              {t("Footer newsletter cta")}
            </button>
          </form>
          <p className="cc-footerNewsletter__disclaimer">
            {t("Footer newsletter disclaimer")}{" "}
            <Link to={paths.privacy} className="cc-footerNewsletter__disclaimerLink">{t("Privacy Policy")}</Link>.
          </p>
          {status === "ok" ? <p className="cc-footerNewsletter__feedback cc-footerNewsletter__feedback--ok" role="status">{t("Subscribed successfully!")}</p> : null}
          {status === "exists" ? <p className="cc-footerNewsletter__feedback cc-footerNewsletter__feedback--err" role="alert">{t("Email already subscribed")}</p> : null}
          {status === "error" ? <p className="cc-footerNewsletter__feedback cc-footerNewsletter__feedback--err" role="alert">{t("Send failed")}</p> : null}
        </div>
        <aside className="cc-footerNewsletter__promo">
          <span className="material-symbols-rounded cc-footerNewsletter__promoIcon" aria-hidden="true">local_shipping</span>
          <div className="cc-footerNewsletter__promoCopy">
            <span className="cc-footerNewsletter__promoTitle">{t("Footer newsletter promo title")}</span>
            <strong className="cc-footerNewsletter__promoText">{t("Footer newsletter promo text")}</strong>
          </div>
        </aside>
      </div>
    </section>
  );
}

function FooterValueBar() {
  const {t} = useI18n();
  return (
    <section className="cc-footerValueBar" aria-label="Store benefits">
      <div className="inside cc-footerValueBar__inner">
        {VALUE_ITEMS.map((item) => (
          <div key={item.key} className="cc-footerValueBar__item">
            <span className="material-symbols-rounded cc-footerValueBar__icon" aria-hidden="true">{item.icon}</span>
            <span className="cc-footerValueBar__text">{t(item.key)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function FooterCategoryLinks() {
  const [categories, setCategories] = useState<Category[]>([]);
  useEffect(() => { getCategoriesCached().then(setCategories).catch(() => {}); }, []);
  if(!categories.length) return null;
  const half = Math.ceil(categories.length / 2);
  const cols = [categories.slice(0, half), categories.slice(half)];
  return (
    <div className="cc-footer__shopCols">
      <ul className="cc-footer__list">
        {cols[0].map((cat) => (
          <li key={cat.id}><FooterLink to={cat.url}>{cat.nume}</FooterLink></li>
        ))}
      </ul>
      {cols[1].length ? (
        <ul className="cc-footer__list">
          {cols[1].map((cat) => (
            <li key={cat.id}><FooterLink to={cat.url}>{cat.nume}</FooterLink></li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function ShopFooter() {
  const {t} = useI18n();
  return (
    <div className="cc-footerZone">
      <FooterNewsletter />
      <FooterValueBar />
      <footer className="cc-footer">
        <div className="inside cc-footer__inner">
          <div className="cc-footer__mainGrid">
            <div className="cc-footer__brandCol">
              <img src="/images/logo-dark.png" alt="CorporatesClub" className="cc-footer__logo" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/images/logo.png"; }} />
              <p className="cc-footer__brandText">{t("Footer brand description")}</p>
              <FooterLink to={paths.contact} className="cc-footer__serviceLink anim">{t("Footer customer service")}</FooterLink>
            </div>
            <div className="cc-footer__linkCol">
              <h3 className="cc-footer__heading">{t("Footer about heading")}</h3>
              <ul className="cc-footer__list">
                {ABOUT_LINKS.map((item) => (
                  <li key={item.to + item.key}><FooterLink to={item.to}>{t(item.key)}</FooterLink></li>
                ))}
              </ul>
            </div>
            <div className="cc-footer__linkCol">
              <h3 className="cc-footer__heading">{t("Footer info heading")}</h3>
              <ul className="cc-footer__list">
                {INFO_LINKS.map((item) => (
                  <li key={item.to + item.key}><FooterLink to={item.to}>{t(item.key)}</FooterLink></li>
                ))}
              </ul>
            </div>
            <div className="cc-footer__shopsCol">
              <h3 className="cc-footer__heading">{t("Footer shops heading")}</h3>
              <FooterCategoryLinks />
            </div>
          </div>
        </div>
      </footer>
      <div className="cc-footerBottom">
        <div className="inside cc-footerBottom__inner">
          <div className="cc-footerBottom__payments">
            <img src="/images/mobilpay.jpg" alt={t("Payment methods")} className="cc-footerBottom__payImg" />
            <div className="cc-footerBottom__legalBadges">
              {ANPC_BADGES.map((badge) => (
                <a key={badge.src} href={badge.href} target="_blank" rel="noreferrer">
                  <img src={badge.src} alt={badge.alt} />
                </a>
              ))}
            </div>
          </div>
          <p className="cc-footerBottom__copy">
            &copy; {new Date().getFullYear()} <b>CORPORATE ANSWER SRL</b>. {t("All rights reserved.")}
          </p>
        </div>
      </div>
    </div>
  );
}
